import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Sparkles, Building2, UploadCloud, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";

export default function RootLanding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", project_details: "", budget: "" });
  const [sending, setSending] = useState(false);
  const [brief, setBrief] = useState(null);
  const [briefUploading, setBriefUploading] = useState(false);
  const briefRef = useRef(null);
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin");
    else if (user && user.role === "creative") {
      if (user.status === "approved") navigate("/portal");
      else if (user.status === "pending_onboarding") navigate("/onboarding");
      else navigate("/pending");
    }
  }, [user, navigate]);

  const inputCls = "w-full bg-[#050505] border border-[#2A2A2A] px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FF4D00] transition-colors placeholder:text-[#555]";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_details) { toast.error("Please fill in your name, email and project details"); return; }
    setSending(true);
    try {
      await api.post("/inquiries", { ...form, brief_file_id: brief?.id || null });
      navigate("/thank-you");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const uploadBrief = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Creative brief must be a PDF"); return; }
    setBriefUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/inquiries/brief-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBrief(data); toast.success("Brief attached");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBriefUploading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[420px] w-full overflow-hidden border-b border-[#2A2A2A] bg-black" data-testid="hero-section">
        <img
          src="/hero-split.jpg"
          alt="Cinematic film set"
          onLoad={() => setHeroLoaded(true)}
          data-testid="hero-bg-image"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${heroLoaded ? "opacity-100" : "opacity-0"}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-[#050505]/30" />
        <div className="absolute top-0 left-0 right-0">
          <div className="max-w-[1400px] mx-auto px-8 lg:px-12 py-5 flex items-center justify-between">
            <img src={LOGO_URL} alt="Splash Creative Hub" className="w-[150px] h-auto object-contain" />
            <Link data-testid="nav-login-link" to="/login" className="text-sm text-white/80 hover:text-white transition-colors px-3 py-2 border border-[#2A2A2A] hover:bg-white/5">Sign in</Link>
          </div>
        </div>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="absolute bottom-0 left-0 right-0">
          <div className="max-w-[1400px] mx-auto px-8 lg:px-12 pb-10">
            <div className="text-xs uppercase tracking-[0.3em] text-[#FF4D00] mb-4">Splash Creative Hub</div>
            <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl leading-[0.9] max-w-4xl">
              WHERE BOLD BRANDS MEET<br /><span className="text-[#FF4D00]">ELITE CREATIVE TALENT</span>
            </h1>
          </div>
        </motion.div>
      </section>

      {/* Split */}
      <section className="max-w-[1400px] mx-auto px-8 lg:px-12 py-16 grid lg:grid-cols-2 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
        {/* Client */}
        <div className="bg-[#0A0A0A] p-8 lg:p-12" data-testid="client-section">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FF4D00] mb-5"><Building2 className="w-4 h-4" /> For Clients</div>
          <h2 className="font-bebas text-4xl lg:text-5xl leading-[0.95] mb-4">HIRE A PRODUCTION-READY CREATIVE CREW</h2>
          <p className="text-sm text-[#A1A1AA] leading-relaxed mb-8 max-w-md">
            Tell us about your project. We'll match you with vetted directors, cinematographers, editors and photographers from the Splash Team — fast.
          </p>

          <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="inquiry-form">
            <input data-testid="inquiry-name-input" className={inputCls} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="inquiry-email-input" type="email" className={inputCls} placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <textarea data-testid="inquiry-details-input" className={`${inputCls} min-h-[120px]`} placeholder="Tell us about your project…" value={form.project_details} onChange={(e) => setForm({ ...form, project_details: e.target.value })} />
            <select data-testid="inquiry-budget-select" className={inputCls} value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })}>
              <option value="">Budget range (optional)</option>
              <option value="Under $2500">Under $2,500</option>
              <option value="$2501-$5000">$2,501 – $5,000</option>
              <option value="$5001-$7500">$5,001 – $7,500</option>
              <option value="$7501+">$7,501+</option>
            </select>
            <div onClick={() => !briefUploading && briefRef.current?.click()} data-testid="inquiry-brief-upload" className={`cursor-pointer border border-dashed p-4 flex items-center gap-3 transition-colors ${brief ? "border-[#10B981] bg-[#10B98110]" : "border-[#2A2A2A] hover:border-[#FF4D00]"}`}>
              <input ref={briefRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={uploadBrief} />
              {briefUploading ? <Loader2 className="w-5 h-5 animate-spin text-[#FF4D00]" /> : brief ? <FileText className="w-5 h-5 text-[#10B981]" /> : <UploadCloud className="w-5 h-5 text-[#A1A1AA]" />}
              <span className="text-sm text-[#A1A1AA] truncate">{brief ? brief.original_filename : "Attach a creative brief (PDF, optional)"}</span>
            </div>
            <button data-testid="inquiry-submit-btn" disabled={sending || briefUploading} className="inline-flex items-center gap-2 bg-[#FF4D00] text-white px-6 py-3 text-sm hover:bg-[#E64500] transition-colors disabled:opacity-60">
              {sending && <Loader2 className="w-4 h-4 animate-spin" />} Send inquiry <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>

        {/* Creative */}
        <div className="bg-[#0A0A0A] p-8 lg:p-12 flex flex-col" data-testid="creative-section">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FF4D00] mb-5"><Sparkles className="w-4 h-4" /> For Creatives</div>
          <h2 className="font-bebas text-4xl lg:text-5xl leading-[0.95] mb-4">JOIN THE SPLASH TEAM</h2>
          <p className="text-sm text-[#A1A1AA] leading-relaxed mb-8 max-w-md">
            An invite-worthy collective of elite freelance creatives. Get matched to premium jobs, manage your work, timesheets and invoices — all in one place.
          </p>
          <div className="mt-auto border border-[#2A2A2A] p-6 hard-shadow-hover bg-gradient-to-br from-[#1a0f08] to-[#0A0A0A]">
            <div className="font-bebas text-3xl tracking-wide mb-2">MEMBERS-ONLY ACCESS</div>
            <ul className="text-sm text-[#A1A1AA] space-y-1 mb-6">
              <li>— Curated, paid creative work</li>
              <li>— One dashboard for jobs, timesheets & invoices</li>
              <li>— Fast onboarding, faster payouts</li>
            </ul>
            <Link data-testid="join-club-btn" to="/join" className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 text-sm hover:bg-[#FF4D00] hover:text-white transition-colors">
              Apply to the Splash Team <ArrowRight className="w-4 h-4" />
            </Link>
            <Link data-testid="check-status-link" to="/status" className="block mt-3 text-xs text-[#A1A1AA] hover:text-white transition-colors">Already applied? Check your status →</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#2A2A2A]">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#A1A1AA]">
          <img src={LOGO_URL} alt="Splash Creative Hub" className="w-[110px] h-auto object-contain" />
          <div className="flex items-center gap-5">
            <Link data-testid="footer-privacy-link" to="/privacy" className="hover:text-white transition-colors">Privacy &amp; Security</Link>
            <span>© 2026 Splash Creative Hub. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
